import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { useLanguage } from '../context/LanguageContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Truck, Anchor, Airplane, Translate } from '@phosphor-icons/react'
import { sendPasswordReset } from '../services/auth'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const { login, sendMagicLink } = useAuth()
  const { language, setLanguage, t } = useLanguage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Pega a URL de origem da query string (sobrevive a redirects externos como Brevo)
  const redirectTo = searchParams.get('redirect') || '/dashboard'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)

    try {
      await login({ email, password })
      // Redireciona para a página de origem ou dashboard
      navigate(redirectTo, { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao fazer login')
    } finally {
      setLoading(false)
    }
  }

  async function handleMagicLink() {
    if (!email) {
      setError('Digite seu email primeiro')
      return
    }

    setError('')
    setMessage('')
    setLoading(true)

    try {
      await sendMagicLink(email)
      setMessage('Link mágico enviado! Verifique seu email.')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao enviar link mágico')
    } finally {
      setLoading(false)
    }
  }

  async function handleForgotPassword() {
    if (!email) {
      setError('Digite seu email primeiro')
      return
    }

    setError('')
    setMessage('')
    setLoading(true)

    try {
      await sendPasswordReset(email)
      setMessage('Se o email estiver cadastrado, você receberá instruções para redefinir sua senha.')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao enviar email de recuperação')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full min-h-screen flex flex-col lg:flex-row" style={{ background: 'rgb(37, 48, 55)' }}>
      {/* Left Side - Branding (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 text-white">
        {/* Logo */}
        <div>
          <div className="mb-8">
            <img
              src="/logo.png"
              alt="Eximtech"
              className="h-14 w-auto"
            />
          </div>

          <div className="mt-12">
            <h2 className="text-4xl font-bold mb-4">Bem-vindo ao Eximtech</h2>
            <p className="text-lg text-slate-300">
              Gestão inteligente de fretes internacionais
            </p>
          </div>
        </div>

        {/* Transport Modes */}
        <div className="flex gap-4">
          <div className="flex-1 bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-green-500/50 transition-all" style={{ '--hover-color': 'rgb(5, 150, 105)' } as any}>
            <Truck size={32} weight="bold" className="mb-3" style={{ color: 'rgb(5, 150, 105)' }} />
            <p className="text-sm font-medium">Terra</p>
          </div>
          <div className="flex-1 bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-green-500/50 transition-all">
            <Anchor size={32} weight="bold" className="mb-3" style={{ color: 'rgb(5, 150, 105)' }} />
            <p className="text-sm font-medium">Mar</p>
          </div>
          <div className="flex-1 bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-green-500/50 transition-all">
            <Airplane size={32} weight="bold" className="mb-3" style={{ color: 'rgb(5, 150, 105)' }} />
            <p className="text-sm font-medium">Ar</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden w-full max-w-md mb-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <img
              src="/logo.png"
              alt="Eximtech"
              className="h-12 w-auto"
            />
          </div>

          <h2 className="text-2xl font-semibold text-white mb-3">{t.login.welcome}</h2>
          <p className="text-slate-300 mb-8">{t.login.subtitle}</p>

          {/* Mobile Transport Icons */}
          <div className="flex justify-center gap-6 mb-8">
            <div className="flex flex-col items-center">
              <div className="bg-slate-800/70 rounded-full p-4 mb-2 border border-slate-700/50">
                <Truck size={24} weight="bold" style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <span className="text-sm text-slate-300">{t.transport.land}</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-slate-800/70 rounded-full p-4 mb-2 border border-slate-700/50">
                <Anchor size={24} weight="bold" style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <span className="text-sm text-slate-300">{t.transport.sea}</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-slate-800/70 rounded-full p-4 mb-2 border border-slate-700/50">
                <Airplane size={24} weight="bold" style={{ color: 'rgb(5, 150, 105)' }} />
              </div>
              <span className="text-sm text-slate-300">{t.transport.air}</span>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="w-full max-w-md">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 lg:p-8 shadow-2xl border border-slate-700/40">
            {/* Header with Language Selector */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">
                {t.login.title}
              </h3>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  className="rounded-lg p-2 transition-all hover:bg-slate-600/50"
                  style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                >
                  <Translate size={20} weight="bold" className="text-white" />
                </button>
                {showLanguageMenu && (
                  <div className="absolute right-0 mt-2 bg-slate-700 rounded-lg shadow-xl border border-slate-600 overflow-hidden z-10">
                    <button
                      type="button"
                      onClick={() => { setLanguage('pt'); setShowLanguageMenu(false) }}
                      className={`block w-full px-4 py-2 text-left text-sm transition-all hover:bg-slate-600 ${
                        language === 'pt' ? 'bg-slate-600 text-white' : 'text-slate-300'
                      }`}
                    >
                      🇧🇷 Português
                    </button>
                    <button
                      type="button"
                      onClick={() => { setLanguage('en'); setShowLanguageMenu(false) }}
                      className={`block w-full px-4 py-2 text-left text-sm transition-all hover:bg-slate-600 ${
                        language === 'en' ? 'bg-slate-600 text-white' : 'text-slate-300'
                      }`}
                    >
                      🇺🇸 English
                    </button>
                    <button
                      type="button"
                      onClick={() => { setLanguage('es'); setShowLanguageMenu(false) }}
                      className={`block w-full px-4 py-2 text-left text-sm transition-all hover:bg-slate-600 ${
                        language === 'es' ? 'bg-slate-600 text-white' : 'text-slate-300'
                      }`}
                    >
                      🇪🇸 Español
                    </button>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="p-4 mb-6 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg text-sm">
                {error}
              </div>
            )}

            {message && (
              <div className="p-4 mb-6 bg-green-500/20 border border-green-500/50 text-green-200 rounded-lg text-sm">
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                    {t.login.email}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                    className="w-full px-3 py-2.5 text-sm rounded-lg bg-white text-slate-900 border-0 focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': 'rgb(5, 150, 105)' } as any}
                    placeholder={t.login.emailPlaceholder}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                    {t.login.password}
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full px-3 py-2.5 text-sm rounded-lg bg-white text-slate-900 border-0 focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': 'rgb(5, 150, 105)' } as any}
                    placeholder={t.login.passwordPlaceholder}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full text-white font-medium py-2.5 px-4 rounded-lg transition-all hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(40, 200, 120)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgb(5, 150, 105)'}
                >
                  {loading ? t.login.loading : t.login.loginButton}
                </button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    className="hover:text-green-300 transition-all disabled:opacity-50"
                    style={{ color: 'rgb(5, 150, 105)' }}
                  >
                    {t.login.forgotPassword}
                  </button>
                  <button
                    type="button"
                    onClick={handleMagicLink}
                    disabled={loading}
                    className="text-slate-300 hover:text-white transition-all disabled:opacity-50"
                  >
                    {t.login.magicLink}
                  </button>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-sm text-slate-400">
                    Não tem uma conta?{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      className="font-medium hover:underline"
                      style={{ color: 'rgb(5, 150, 105)' }}
                    >
                      Criar conta gratuitamente
                    </button>
                  </p>
                </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
